import { Alert, Typography, AlertProps, Paper, TableContainer, Table, TableBody, Grid } from '@mui/material';
import { Box } from '@mui/system';
import './Simulation.css';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, ChangeEvent } from 'react';
import { fetchCompatibilityTargetSyllabuses, fetchEquivalencyRuleActivity, fetchSourceInstitutions, fetchSourceSyllabuses, fetchTargetSyllabuses, reset, resetSourceSyllabuses } from '../Request/requestSlice';
import SimulationTable from './SimulationTable/SimulationTable';
import CreatablePaginateAutocomplete from '../../components/CreatablePaginateAutocomplete/CreatablePaginateAutocomplete';
import { useAppDispatch, useAppSelector } from '../../shared/redux/store';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import Syllabus, { CompatibleSyllabusSearchParams, SuggestedSyllabus, SyllabusSearchParams } from '../../models/Syllabus';
import Institution, { InstitutionSearchParams } from '../../models/Institution';
import { EquivalencyRuleSearchParams } from '../../models/EquivalencyRule';
import { CreatablePaginateAutocompleteOptionClass, CreatablePaginateAutocompleteProps } from '../../components/CreatablePaginateAutocomplete/CreatablePaginateAutocompleteProps';
import ExploreRules from '../../models/EquivalencyRuleActivity';
import EmptyMessage from '../../components/Alerts/EmptyMessage';
import SuggestionRow from '../Request/SuggestionRow/SuggestionRow';


export default function Simulation() {
  const isRowSelected = (row: SuggestedSyllabus | Syllabus): boolean => {
    return selected?.id === row.id;
  };
  const selectRow = (
    e: ChangeEvent<HTMLInputElement>,
    row: SuggestedSyllabus | Syllabus
  ) => {
    if (e.target.checked) {
      setTargetSyllabousSelection(row);
    } else {
      setTargetSyllabousSelection(null);
    }
  };
  const loading = false;
  let hiddenNavbar = false;
  const { t } = useTranslation();

  const [sourceInstitution, setSourceInstitution] =
  useState<Institution | null>(null);
  const [sourceSyllabus, setSourceSyllabus] = useState<Syllabus | null>(null);
  const [targetSyllabus, setTargetSyllabus] = useState<Syllabus | null>(null);
  const [equivalencies, setEquivalencies] = useState<ExploreRules[] | null>(
    null
  );

  const optionsSourceInstitutions = useAppSelector((state) => state.request.sourceInstitutions);
  const targetInstitution = useAppSelector((state) => state.request.targetInstitution);
  const optionsSourceSyllabuses = useAppSelector((state) => state.request.sourceSyllabuses);
  const optionsTargetSyllabuses = useAppSelector((state) => state.request.targetSyllabuses);
  const equivalencyRules = useAppSelector((state) => state.request);
  const compatibleTargetSyllabuses = useAppSelector((state) => state.request.compatibilityTargetSyllabuses);
  const [optionsCompatibleSyllabuses, setOptionsCompatibleSyllabuses] = useState<SuggestedSyllabus[]>([]);
  const [selected, setSelected] = useState<SuggestedSyllabus | null>(null);

  const setTargetSyllabousSelection = (syllabus: any) => {
    setSelected(syllabus);
    setTargetSyllabus(syllabus);
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (compatibleTargetSyllabuses && compatibleTargetSyllabuses.length > 0) {
      setOptionsCompatibleSyllabuses(
        compatibleTargetSyllabuses.map((syllabus) => {
          return {
            ...syllabus,
            id: syllabus.targetSyllabusId,
            name: syllabus.targetSyllabusName,
          };
        })
      );
    } else {
      setOptionsCompatibleSyllabuses([]);
    }
  }, [compatibleTargetSyllabuses]);

  const institutionCreatablePaginateAutocompleteProps: CreatablePaginateAutocompleteProps =
  {
    options: optionsSourceInstitutions.map((institution: Institution) =>
      CreatablePaginateAutocompleteOptionClass.fromInstitution(institution)
    ),
    value: sourceInstitution
      ? CreatablePaginateAutocompleteOptionClass.fromInstitution(
        sourceInstitution
      )
      : null,
    label: t('institutionsSource'),
    noOptionsText: t('noOptionsTextInstitutions'),
    onChange: () => changeSourceInstitution(),
    setValue: (value) => {
      const institution =
        CreatablePaginateAutocompleteOptionClass.toInstitution(
          value,
          optionsSourceInstitutions
        );
      setSourceInstitution(
        institution ?? null
      );
    },
    fetchFunc: (params: InstitutionSearchParams) =>
      onFetchSourceInstitutions(params),
    maxLength: 200,
    testId: 'selectInstitution',
    addOption: false
  };

  const syllabusCreatablePaginateAutocompleteProps: CreatablePaginateAutocompleteProps =
  {
    required: true,
    options: optionsSourceSyllabuses.map((syllabus: Syllabus) =>
      CreatablePaginateAutocompleteOptionClass.fromSyllabus(syllabus)
    ),
    value: sourceSyllabus
      ? CreatablePaginateAutocompleteOptionClass.fromSyllabus(sourceSyllabus)
      : null,
    placeholder: t('planSource'),
    label: sourceInstitution ? t('planSource') : t('institutionIdNeeded'),
    disableCondition: !sourceInstitution,
    noOptionsText: t('noOptionsTextSyllabuses'),
    onChange: () => changeSourceSyllabus(),
    setValue: (value) => {
      const syllabus = CreatablePaginateAutocompleteOptionClass.toSyllabus(
        value,
        optionsSourceSyllabuses
      );
      setSourceSyllabus(syllabus ?? null);
    },
    fetchFunc: (params: SyllabusSearchParams) =>
      onFetchSourceSyllabus(params),
    maxLength: 200,
    testId: 'selectSyllabus',
    addOption: false
  };

  const targetSyllabusCreatablePaginateAutocompleteProps: CreatablePaginateAutocompleteProps =
  {
    required: true,
    options: optionsTargetSyllabuses.map((syllabus: Syllabus) =>
      CreatablePaginateAutocompleteOptionClass.fromSyllabus(syllabus)
    ),
    value: targetSyllabus
      ? CreatablePaginateAutocompleteOptionClass.fromSyllabus(targetSyllabus)
      : null,
    placeholder: t('planTarget'),
    label: sourceSyllabus ? t('planTarget') : t('institutionIdNeeded'),
    disableCondition: !sourceSyllabus,
    noOptionsText: t('noOptionsTextSyllabuses'),
    onChange: () => { changeTargetSyllabus() },
    setValue: (value) => {
      const syllabus = CreatablePaginateAutocompleteOptionClass.toSyllabus(
        value,
        optionsTargetSyllabuses
      );
      setTargetSyllabousSelection(value);
      setTargetSyllabus(syllabus ?? null);
    },
    fetchFunc: (params: SyllabusSearchParams) =>
      onFetchTargetSyllabus(params),
    maxLength: 200,
    testId: 'selectTargetSyllabus',
    addOption: false
  };

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, []);
  useEffect(() => {
    if (targetSyllabus?.id) {
      handleExploreRules();
    }
  }, [targetSyllabus]);

  useEffect(() => {
    if (equivalencyRules?.rules?.length > 0) {
      setEquivalencies(equivalencyRules.rules);
    }
    else {
      setEquivalencies([]);
    }
  }, [equivalencyRules]);

  useEffect(() => {
    if (sourceSyllabus?.id) {
      compatibilityTarget();
    }
  }, [sourceSyllabus]);

  const parameters: EquivalencyRuleSearchParams = {
    sourceSyllabusId: sourceSyllabus?.id,
    offset: 0,
    pageSize: 100,
  };

  const compatibleSyllabusesParams: CompatibleSyllabusSearchParams = {
    institutionId: sourceInstitution?.id || '',
    canBeTarget: true,
    compatibilityCheck: true,
    sourceSyllabusId: sourceSyllabus?.id || '',
  };

  const handleExploreRules = async () => {
    dispatch(
      fetchEquivalencyRuleActivity({
        ...parameters,
        sourceSyllabusId: sourceSyllabus?.id,
        targetSyllabusId: targetSyllabus?.id,
      })
    ).catch((e) => console.log(e));

    await dispatch(
      fetchCompatibilityTargetSyllabuses(compatibleSyllabusesParams)
    ).catch((e: any) => console.log(e));
  };

  const compatibilityTarget = async () => {

    await dispatch(
      fetchCompatibilityTargetSyllabuses(compatibleSyllabusesParams)
    ).catch((e: any) => console.log(e));
  };

  const changeSourceInstitution = () => {
    setSourceSyllabus(null);
    setTargetSyllabus(null);
    dispatch(resetSourceSyllabuses());
  };

  const changeSourceSyllabus = () => {
    setTargetSyllabus(null);
    setTargetSyllabus(null);
  };

  const changeTargetSyllabus = () => {
    setEquivalencies(null);
  };

  const onFetchSourceInstitutions = (params: InstitutionSearchParams) => {
    dispatch(fetchSourceInstitutions({ parameters: params })).catch((e) => console.log(e));
  };

  const onFetchSourceSyllabus = (params: SyllabusSearchParams) => {
    if (sourceInstitution?.id) {
      const parameters = { ...params, institutionId: sourceInstitution.id };
      dispatch(fetchSourceSyllabuses(parameters)).catch((e) => console.log(e));
    }
  };

  const onFetchTargetSyllabus = (params: SyllabusSearchParams) => {
    if (targetInstitution?.id) {
      const parameters = {
        ...params,
        institutionId: targetInstitution.id,
        canBeTarget: true,
      };
      dispatch(fetchTargetSyllabuses(parameters)).catch((e) => console.log(e));
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      className="REQXXI-Stepper-Container fadeIn"
    >
      <Grid item className="REQXXI-Stepper-Header">
        <Grid
          container
          justifyContent="center"
          alignContent="center"
          style={{ height: '100%', display: 'flex' }}
        >
          <Typography
            className="REQXXI-Search-Title"
            color="primary"
            variant="h4"
            fontWeight="500"
          >
            {t('equivalencyExploreTitle')}
          </Typography>
        </Grid>
      </Grid>

      <Grid
        tabIndex={-1}
        item
        className={
          hiddenNavbar
            ? 'REQXXI-Stepper-Consult-Request-HiddenNavbar'
            : 'REQXXI-Stepper-Consult-Request'
        }
      >
        <div style={{ height: 'calc(100vh - 68px - 113px)' }}>
          <Grid
            container
            direction="column"
            alignContent={'center'}
            height={'100%'}
            className="REQXXI-Step-Container-Component"
          >
            <Grid item height={'inherit'} width={'90%'}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                height={'inherit'}
                wrap="nowrap"
                className="REQXXI-Container-Component"
              >
                <Grid
                  item
                  xs={12}
                  lg={5.5}
                  height={'inherit'}
                  className="REQXXI-Grid-Paper"
                  mr={'10px'}
                  sx={{ mb: { xs: '20px', sm: '0px' } }}
                >
                  <Box
                    className="REQXXI-Block-Container"
                    style={{ borderRadius: '20px', padding: '40px', overflowY: 'auto' }}
                    height={'inherit'}
                  >
                    <Box
                      sx={{
                        maxHeight: '60vh',
                        p: '0 0 24px',
                        overflowY: 'auto',
                        maxWidth: 700,
                        width: '100%',
                      }}
                    >
                      {t('consultEquivalencyIntro')}
                    </Box>
                    <CreatablePaginateAutocomplete
                      {...institutionCreatablePaginateAutocompleteProps}

                    />
                    <CreatablePaginateAutocomplete
                      {...syllabusCreatablePaginateAutocompleteProps}
                      data-testid="selectSyllabus"
                    />

                    {sourceSyllabus && !!optionsTargetSyllabuses && (
                      <>
                        <CreatablePaginateAutocomplete
                          {...targetSyllabusCreatablePaginateAutocompleteProps}
                        />

                        <Typography
                          variant="body1"
                          color="initial"
                          style={{ marginTop: '15px' }}
                        >
                          {t('titleSuggestedSyllabousTable')}
                        </Typography>
                        <Paper variant="outlined">
                          <TableContainer
                            sx={{ height: '300px'}}
                          >
                            <Table
                              stickyHeader
                              className="REQXXI-Request-Table"
                              size="small"
                              aria-label="customized table"
                            >
                              <TableBody
                                className="REQXXI-Request-TableBody"
                                style={{ overflow: 'auto' }}
                              >
                                {!loading &&
                                  optionsCompatibleSyllabuses?.map((row, i) => (
                                    <SuggestionRow
                                      key={row ? row.id : i}
                                      row={row}
                                      isRowSelected={isRowSelected}
                                      selectRow={selectRow}
                                    />
                                  ))}
                              </TableBody>
                            </Table>
                            {!loading &&
                              optionsCompatibleSyllabuses &&
                              optionsCompatibleSyllabuses?.length < 1 && (
                                <Box
                                  sx={{
                                    height: '230px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                  }}
                                >
                                  <EmptyMessage />
                                </Box>
                              )}
                            {loading && (
                              <Box
                                sx={{
                                  height: '230px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  textAlign: 'center',
                                }}
                              >
                                <Typography variant="body1" color="initial">
                                  {t('loadingCompatibleSyllabuses')}
                                </Typography>
                              </Box>
                            )}
                          </TableContainer>
                        </Paper>

                        {!loading &&
                          optionsCompatibleSyllabuses &&
                          optionsCompatibleSyllabuses?.length < 1 && (
                            <Typography
                              color="#949494"
                              sx={{ fontSize: '12px', textAlign: 'center' }}
                            >
                              {t('infoSuggestedSyllabus')}
                            </Typography>
                          )}
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={5.5}
                  height={'inherit'}
                  className="REQXXI-Grid-Paper"
                  mr={'10px'}
                  sx={{ width: { xs: '100%' } }}
                >
                  <div
                    className="REQXXI-Request-Accordion"
                    style={{ height: '100%' }}
                  >
                    <div
                      className="REQXXI-Block-Container"
                      style={{ height: '100%', borderRadius: '20px' }}
                    >
                      {targetSyllabus ? (
                        <>
                          {!!equivalencies && equivalencies.length > 0 ? (
                            <SimulationTable rules={equivalencies} />
                          ) : (
                            <Alert
                              color={'primary' as AlertProps['color']}
                              severity="info"
                              style={{ margin: '16px', background: '#FFF' }}
                            >
                              {t('equivalencyConsultEmptySimulation')}
                            </Alert>
                          )}
                        </>
                      ) : (

                        <Grid item height={'inherit'} display={'flex'} alignItems={'center'} justifyContent={'center'} >
                          <Typography align="center">
                            <>
                              <TouchAppIcon
                                style={{ fontSize: '5rem' }}
                                color="secondary"
                              />
                              <br />
                              <br />
                              {t('simulateEquivalences')}
                            </>
                          </Typography>
                        </Grid>

                      )}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>
    </Grid>
  );
}
